import { erc20Abi } from "viem";
import {
  writeContract,
  readContract,
  simulateContract,
  waitForTransactionReceipt,
  getBalance,
} from "@wagmi/core";
import { wagmiConfig } from "@/config";
import { PaymentGatewayAbi } from "@/abi/PaymentGatewayAbi.abi";
import { PAYMENT_GATEWAY_CONTRACT_ADDRESS } from "@/config/env";

/**
 * 校验用户余额是否足够
 * @param walletAddress 用户钱包地址
 * @param usdtAddress USDT 合约地址
 * @param usdtAmount 需要支付的 USDT 数量（字符串，最小单位）
 * @param feeBnb 需要支付的 BNB 手续费（字符串，最小单位）
 * @returns [是否通过校验, 错误信息]
 */
export async function checkBalance({
  walletAddress,
  usdtAddress,
  usdtAmount,
  usddAddress,
  usddAmount,
  feeBnb,
}: {
  walletAddress: `0x${string}`;
  usdtAddress: `0x${string}`;
  usdtAmount: string;
  usddAddress: `0x${string}`;
  usddAmount: string;
  feeBnb: string;
}): Promise<[boolean, string | null]> {
  try {
    // 0. 验证参数
    if (!usdtAddress || usdtAddress === "0x") {
      console.error("无效的 地址:", usdtAddress);
      return [false, "toast.chainError.balanceCheckFailed"];
    }

    // 1. 获取 BNB 余额
    const bnbBalance = await getBalance(wagmiConfig, {
      address: walletAddress,
    });

    // 2. 获取 USDT 余额
    const usdtBalance = await readContract(wagmiConfig, {
      address: usdtAddress,
      abi: erc20Abi,
      functionName: "balanceOf",
      args: [walletAddress],
    });

    const usddBalance = await readContract(wagmiConfig, {
      address: usddAddress,
      abi: erc20Abi,
      functionName: "balanceOf",
      args: [walletAddress],
    });

    // 3. 估算 Gas 费用（大约）
    // 为了安全起见，我们预留一些额外的 Gas 费用
    // 通常一笔交易需要 21000 Gas，复杂交易可能需要更多
    // 按当前 Gas Price 估算，预留 0.001 BNB 作为 Gas 费用
    const estimatedGasFee = BigInt("100000000000000"); // 0.0001 BNB

    const requiredBnb = BigInt(feeBnb) + estimatedGasFee;
    const requiredUsdt = BigInt(usdtAmount);
    const requiredUsdd = BigInt(usddAmount);

    console.log("余额校验:", {
      walletAddress: walletAddress,
      bnbBalance: bnbBalance.value.toString(),
      usdtBalance: usdtBalance.toString(),
      usddBalance: usddBalance.toString(),
      requiredBnb: requiredBnb.toString(),
      requiredUsdt: requiredUsdt.toString(),
      requiredUsdd: requiredUsdd.toString(),
      feeBnb,
      estimatedGasFee: estimatedGasFee.toString(),
    });

    // 4. 校验 BNB 余额（包括手续费 + Gas 费）
    if (bnbBalance.value < requiredBnb) {
      return [false, "toast.chainError.insufficientBNB"];
    }

    // 5. 校验 USDT 余额
    if ((usdtBalance as bigint) < requiredUsdt) {
      return [false, "toast.chainError.insufficientBalance"];
    }

    // 6. 校验 UDD 余额
    if ((usddBalance as bigint) < requiredUsdd) {
      return [false, "toast.chainError.insufficientBalance"];
    }

    return [true, null];
  } catch (err: any) {
    console.error("余额校验失败:", err);
    return [false, "toast.chainError.balanceCheckFailed"];
  }
}
/**
 * 校验用户余额是否足够
 * @param walletAddress 用户钱包地址
 * @param usdtAddress USDT 合约地址
 * @param usdtAmount 需要支付的 USDT 数量（字符串，最小单位）
 * @param feeBnb 需要支付的 BNB 手续费（字符串，最小单位）
 * @returns [是否通过校验, 错误信息]
 */
export async function checkSingleCoinBalance({
  walletAddress,
  address,
  amount,
  feeBnb,
}: {
  walletAddress: `0x${string}`;
  address: `0x${string}`;
  amount: string;
  feeBnb: string;
}): Promise<[boolean, string | null]> {
  try {
    // 0. 验证参数
    if (!address || address === "0x") {
      console.error("无效的 USDT 地址:", address);
      return [false, "toast.chainError.balanceCheckFailed"];
    }

    // 1. 获取 BNB 余额
    const bnbBalance = await getBalance(wagmiConfig, {
      address: walletAddress,
    });

    // 2. 获取 USDT 余额
    const usdtBalance = await readContract(wagmiConfig, {
      address: address,
      abi: erc20Abi,
      functionName: "balanceOf",
      args: [walletAddress],
    });

    // 3. 估算 Gas 费用（大约）
    // 为了安全起见，我们预留一些额外的 Gas 费用
    // 通常一笔交易需要 21000 Gas，复杂交易可能需要更多
    // 按当前 Gas Price 估算，预留 0.001 BNB 作为 Gas 费用
    const estimatedGasFee = BigInt("100000000000000"); // 0.0001 BNB

    const requiredBnb = BigInt(feeBnb) + estimatedGasFee;
    const requiredUsdt = BigInt(amount);

    console.log("余额校验:", {
      walletAddress: walletAddress,
      bnbBalance: bnbBalance.value.toString(),
      usdtBalance: usdtBalance.toString(),
      requiredBnb: requiredBnb.toString(),
      requiredUsdt: requiredUsdt.toString(),
      feeBnb,
      estimatedGasFee: estimatedGasFee.toString(),
    });

    // 4. 校验 BNB 余额（包括手续费 + Gas 费）
    if (bnbBalance.value < requiredBnb) {
      console.log("BNB 余额不足:", {
        bnbBalance: bnbBalance.value.toString(),
        requiredBnb: requiredBnb.toString(),
        feeBnb,
        estimatedGasFee: estimatedGasFee.toString(),
      });
      return [false, "toast.chainError.insufficientBNB"];
    }

    // 5. 校验 USDT 余额
    if ((usdtBalance as bigint) < requiredUsdt) {
      console.log("USDT 余额不足:", {
        usdtBalance: usdtBalance.toString(),
        requiredUsdt: requiredUsdt.toString(),
        feeBnb,
        address,
        estimatedGasFee: estimatedGasFee.toString(),
      });
      return [false, "toast.chainError.insufficientBalance"];
    }

    return [true, null];
  } catch (err: any) {
    console.error("余额校验失败:", err);
    return [false, "toast.chainError.balanceCheckFailed"];
  }
}

/** 自动 approve（如果 allowance 不够） */
async function maybeApprove({
  owner,
  tokenAddress,
  amount,
  spender,
  decimals = 18,
}: {
  owner: `0x${string}`;
  tokenAddress: `0x${string}`;
  amount: string;
  spender: `0x${string}`;
  decimals?: number;
}): Promise<[string | null, Error | null]> {
  try {
    const currentAllowance = await readContract(wagmiConfig, {
      address: tokenAddress,
      abi: erc20Abi,
      functionName: "allowance",
      args: [owner, spender],
    });

    const needed = BigInt(amount);

    if ((currentAllowance as bigint) >= needed) {
      return [null, null]; // 已经够用，不需要 approve
    }

    const tx = await writeContract(wagmiConfig, {
      address: tokenAddress,
      abi: erc20Abi,
      functionName: "approve",
      args: [spender, needed],
    });

    // 等待链上确认
    const receipt = await waitForTransactionReceipt(wagmiConfig, {
      hash: tx,
    });

    // 返回 transaction hash
    return [receipt.transactionHash, null];
  } catch (err: any) {
    return [null, err];
  }
}

/**
 * 批量自动 approve 多个代币（如果 allowance 不够）
 * @param owner 授权者地址
 * @param tokens 代币信息数组（地址、授权数量、spender、小数位数）
 * @returns 授权结果：[交易哈希数组, 错误信息]
 */
async function maybeBatchApprove({
  owner,
  tokens,
}: {
  owner: `0x${string}`;
  tokens: {
    address: `0x${string}`;
    amount: string;
    spender: `0x${string}`;
    decimals?: number;
  }[];
}): Promise<[string[] | null, Error | null]> {
  try {
    const approveSingle = async (token: {
      address: `0x${string}`;
      amount: string;
      spender: `0x${string}`;
      decimals?: number;
    }): Promise<string | null> => {
      const decimals = token.decimals ?? 18;
      const currentAllowance = await readContract(wagmiConfig, {
        address: token.address,
        abi: erc20Abi,
        functionName: "allowance",
        args: [owner, token.spender],
      });

      const needed = BigInt(token.amount) * BigInt(10 ** decimals);

      if ((currentAllowance as bigint) >= needed) {
        return null;
      }

      const txHash = await writeContract(wagmiConfig, {
        address: token.address,
        abi: erc20Abi,
        functionName: "approve",
        args: [token.spender, needed],
      });

      const receipt = await waitForTransactionReceipt(wagmiConfig, {
        hash: txHash,
      });

      return receipt.transactionHash;
    };

    const txHashes: string[] = [];
    for (const token of tokens) {
      const hash = await approveSingle(token);
      if (hash) txHashes.push(hash);
    }

    return [txHashes, null];
  } catch (err: any) {
    return [null, err];
  }
}

/** 使用 Pay 转 USDT + BNB 手续费 */
export async function sendTokenWithBNBFeeUsePayContract({
  sender,
  tokenAddress,
  amount,
  recipient,
  orderId,
  feeBNB = "0",
  decimals = 18,
}: {
  sender: `0x${string}`;
  tokenAddress: `0x${string}`;
  amount: string;
  recipient: `0x${string}`;
  orderId: string;
  feeBNB?: string;
  decimals?: number;
}): Promise<[string | null, Error | null]> {
  console.log("参数：", { sender, amount, feeBNB, tokenAddress, recipient });

  try {
    // 1️⃣ 自动 approve
    const [approveTx, approveErr] = await maybeApprove({
      owner: sender,
      tokenAddress,
      amount,
      spender: PAYMENT_GATEWAY_CONTRACT_ADDRESS as `0x${string}`,
      decimals,
    });

    if (approveErr) return [null, approveErr];

    if (approveTx) console.log("approve tx hash:", approveTx);

    // 3️⃣ simulate
    const req = await simulateContract(wagmiConfig, {
      address: PAYMENT_GATEWAY_CONTRACT_ADDRESS as `0x${string}`,
      abi: PaymentGatewayAbi,
      functionName: "pay",
      args: [recipient, BigInt(feeBNB), tokenAddress, BigInt(amount), orderId],
      value: BigInt(feeBNB),
    });

    console.log("simulate:", req);

    // 4️⃣ send tx
    const hash = await writeContract(wagmiConfig, req.request);
    return [hash, null];
  } catch (err: any) {
    return [null, err];
  }
}

/**
 * 充值：将 AGI 通过 PaymentGateway 转至平台收款地址（自动 approve + pay）
 */
export async function sendTokenDepositUsePayContract({
  sender,
  tokenAddress,
  recipient,
  feeBNB = "0",
  amountWei,
  orderId,
}: {
  sender: `0x${string}`;
  tokenAddress: `0x${string}`;
  recipient: `0x${string}`;
  feeBNB?: string;
  amountWei: string;
  orderId: string;
}): Promise<[string | null, Error | null]> {
  try {
    const [approveTx, approveErr] = await maybeApprove({
      owner: sender,
      tokenAddress,
      amount: amountWei,
      spender: PAYMENT_GATEWAY_CONTRACT_ADDRESS as `0x${string}`,
    });
    if (approveErr) return [null, approveErr];
    if (approveTx) console.log("recharge approve tx hash:", approveTx);

    const req = await simulateContract(wagmiConfig, {
      address: PAYMENT_GATEWAY_CONTRACT_ADDRESS as `0x${string}`,
      abi: PaymentGatewayAbi,
      functionName: "deposit",
      args: [
        recipient,
        BigInt(feeBNB),
        tokenAddress,
        BigInt(amountWei),
        orderId,
      ],
      value: BigInt(feeBNB),
    });
    const hash = await writeContract(wagmiConfig, req.request);
    return [hash, null];
  } catch (err: any) {
    console.error("充值失败:", err);
    return [null, err];
  }
}
